import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Icon } from "./Icon";

const BotAuthCallback: React.FC = () => {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isExchanging, setIsExchanging] = useState(false);
  const navigate = useNavigate();

  const exchangeCodeForTokensDirectly = async (code: string, state: string) => {
    setIsExchanging(true);
    try {
      // Decode state to get bot username
      const stateData = JSON.parse(atob(state));
      const { botUsername } = stateData;

      // Call the public endpoint directly - no auth required
      const response = await axios.post(
        `/api/bot/credentials/${botUsername === "auto-detect" ? "auto-detect" : botUsername}/public-exchange`,
        { code, state },
      );

      // Success! Show success message with actual bot username
      const actualBotUsername = response.data.botUsername || botUsername;
      window.location.href = `/auth/bot-callback?success=true&bot=${actualBotUsername}`;
    } catch (error: any) {
      console.error("Failed to exchange code:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Failed to exchange code for tokens",
      );
      setIsExchanging(false);
    }
  };

  useEffect(() => {
    // Check for authorization code in query params (for refresh token flow)
    const queryParams = new URLSearchParams(window.location.search);
    const code = queryParams.get("code");
    const state = queryParams.get("state");
    const queryError = queryParams.get("error");

    // Check for access token in hash (for implicit flow)
    const hash = window.location.hash.substring(1);
    const hashParams = new URLSearchParams(hash);
    const accessToken = hashParams.get("access_token");
    const hashError = hashParams.get("error");

    if (code && state) {
      // Authorization code flow - automatically exchange for tokens
      setError(null);
      setToken(code);
      setIsCode(true);
      // Always try to exchange the code automatically
      exchangeCodeForTokensDirectly(code, state);
    } else if (accessToken) {
      // Implicit flow - direct access token
      setToken(accessToken);
      setIsCode(false);
    } else if (queryError || hashError) {
      setError(`Authorization failed: ${queryError || hashError}`);
    } else {
      setError("No access token or authorization code found in callback URL");
    }
  }, []);

  const _exchangeCodeForTokens = async (code: string, state: string) => {
    setIsExchanging(true);
    try {
      // Decode state to get bot username
      const stateData = JSON.parse(atob(state));
      const { botUsername } = stateData;

      // Get the correct auth token from localStorage
      const authToken = localStorage.getItem("access_token");
      if (!authToken) {
        // Since we're in a private window, we can't access the main window's localStorage
        // Show success message with instructions instead
        setIsExchanging(false);
        setError(null);
        return;
      }

      const _response = await axios.post(
        `/api/bot/credentials/${botUsername}/exchange-code`,
        { code },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );

      // Success! Redirect to bot admin with success message
      navigate(`/dashboard/admin/bots?success=true&bot=${botUsername}`);
    } catch (error: any) {
      console.error("Failed to exchange code:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Failed to exchange code for tokens",
      );
      setIsExchanging(false);
    }
  };

  const handleCopyToken = () => {
    if (token) {
      navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleReturnToAdmin = () => {
    navigate("/dashboard/admin/bots");
  };

  // Check if this is a success redirect from backend
  const urlParams = new URLSearchParams(window.location.search);
  const isSuccess = urlParams.get("success") === "true";
  const botUsernameFromUrl = urlParams.get("bot");

  // If it's a success redirect, show success message
  if (isSuccess && botUsernameFromUrl) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#0f1419",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <div
          style={{
            backgroundColor: "#1a202c",
            padding: "40px",
            borderRadius: "12px",
            maxWidth: "600px",
            width: "100%",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.5)",
            border: "1px solid #2d3748",
          }}
        >
          <h1
            style={{
              color: "#9146FF",
              marginTop: 0,
              marginBottom: "20px",
              fontSize: "28px",
              textAlign: "center",
            }}
          >
            Bot Credentials Updated!
          </h1>

          <div
            style={{
              backgroundColor: "#48BB78",
              padding: "20px",
              borderRadius: "8px",
              marginBottom: "20px",
              textAlign: "center",
            }}
          >
            <h3 style={{ color: "white", marginTop: 0 }}>
              <Icon
                name="check"
                size={18}
                color="#10B981"
                style={{ verticalAlign: "-3px", marginRight: "8px" }}
              />
              Success!
            </h3>
            <p style={{ color: "white", marginBottom: 0 }}>
              Bot credentials for <strong>{botUsernameFromUrl}</strong> have
              been updated successfully.
            </p>
          </div>

          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "20px",
              borderRadius: "8px",
              fontSize: "16px",
              color: "#cbd5e0",
              textAlign: "center",
            }}
          >
            <p style={{ margin: 0 }}>
              You can close this window & return to the admin now
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#0f1419",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "#1a202c",
          padding: "40px",
          borderRadius: "12px",
          maxWidth: "600px",
          width: "100%",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.5)",
          border: "1px solid #2d3748",
        }}
      >
        <h1
          style={{
            color: "#9146FF",
            marginTop: 0,
            marginBottom: "20px",
            fontSize: "28px",
            textAlign: "center",
          }}
        >
          Bot Token Generation
        </h1>

        {error && (
          <div
            style={{
              backgroundColor: "#c53030",
              color: "white",
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "20px",
              textAlign: "center",
            }}
          >
            {error}
          </div>
        )}

        {/* Show processing message while we wait for automatic exchange */}
        {token && isCode && !localStorage.getItem("access_token") && !error && (
          <div>
            <div
              style={{
                backgroundColor: "#3182CE",
                padding: "30px",
                borderRadius: "8px",
                marginBottom: "20px",
                textAlign: "center",
              }}
            >
              <h3 style={{ color: "white", marginTop: 0 }}>⏳ Processing...</h3>
              <p style={{ color: "white", marginBottom: 0 }}>
                Updating bot credentials for{" "}
                {(() => {
                  try {
                    const stateParam = new URLSearchParams(
                      window.location.search,
                    ).get("state");
                    if (stateParam) {
                      const stateData = JSON.parse(atob(stateParam));
                      return stateData.botUsername || "the bot";
                    }
                  } catch (e) {}
                  return "the bot";
                })()}
                ...
              </p>
              <p
                style={{ color: "white", marginTop: "10px", fontSize: "14px" }}
              >
                This should happen automatically. If you&apos;re not redirected
                in a few seconds, try refreshing the page.
              </p>
            </div>

            <div
              style={{
                backgroundColor: "#2d3748",
                padding: "20px",
                borderRadius: "8px",
                fontSize: "14px",
                color: "#cbd5e0",
                textAlign: "center",
              }}
            >
              <p style={{ margin: 0 }}>
                The authorization code is being exchanged for access tokens
                automatically. You&apos;ll be redirected to a success page once
                complete.
              </p>
            </div>
          </div>
        )}

        {isExchanging && (
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "40px",
              borderRadius: "8px",
              marginBottom: "20px",
              textAlign: "center",
            }}
          >
            <h3 style={{ color: "#9146FF", marginTop: 0 }}>
              Exchanging code for tokens...
            </h3>
            <p style={{ color: "#cbd5e0" }}>
              Please wait while we exchange your authorization code for access
              and refresh tokens.
            </p>
          </div>
        )}

        {token && !isExchanging && localStorage.getItem("access_token") && (
          <>
            <div
              style={{
                backgroundColor: "#2d3748",
                padding: "20px",
                borderRadius: "8px",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  color: "#48BB78",
                  marginTop: 0,
                  marginBottom: "15px",
                }}
              >
                <Icon
                  name="check"
                  size={16}
                  color="#10B981"
                  style={{ verticalAlign: "-3px", marginRight: "6px" }}
                />
                {isCode ? "Authorization Code" : "Access Token"} Generated
                Successfully!
              </h3>
              <p
                style={{
                  color: "#cbd5e0",
                  marginBottom: "15px",
                  fontSize: "14px",
                }}
              >
                {isCode ? (
                  <>
                    You received an authorization code. You need to exchange
                    this code for access and refresh tokens using the curl
                    command shown in the Bot Admin instructions.
                  </>
                ) : (
                  <>
                    Your bot access token has been generated. Copy it and paste
                    it into the Bot Admin form.
                  </>
                )}
              </p>
              <div
                style={{
                  backgroundColor: "#1a202c",
                  padding: "15px",
                  borderRadius: "6px",
                  wordBreak: "break-all",
                  fontFamily: "monospace",
                  fontSize: "13px",
                  color: "#e2e8f0",
                  marginBottom: "15px",
                  border: "1px solid #4a5568",
                }}
              >
                {token}
              </div>
              <button
                onClick={handleCopyToken}
                style={{
                  width: "100%",
                  padding: "12px",
                  backgroundColor: copied ? "#48BB78" : "#9146FF",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "16px",
                  fontWeight: "500",
                  transition: "background-color 0.2s",
                }}
              >
                {copied ? (
                  <>
                    <Icon
                      name="check"
                      size={14}
                      color="#10B981"
                      style={{ verticalAlign: "-2px", marginRight: "4px" }}
                    />
                    Copied!
                  </>
                ) : isCode ? (
                  "Copy Code"
                ) : (
                  "Copy Token"
                )}
              </button>
            </div>

            <div
              style={{
                backgroundColor: "#2d3748",
                padding: "15px",
                borderRadius: "8px",
                marginBottom: "20px",
                fontSize: "13px",
                color: "#fbbf24",
              }}
            >
              <strong>
                <Icon
                  name="alert-triangle"
                  size={14}
                  style={{ verticalAlign: "-2px", marginRight: "6px" }}
                />
                Important:
              </strong>
              <ul
                style={{
                  marginTop: "10px",
                  marginBottom: 0,
                  paddingLeft: "20px",
                }}
              >
                {isCode ? (
                  <>
                    <li>
                      This code expires in 5 minutes - exchange it quickly!
                    </li>
                    <li>
                      You&apos;ll need your Client Secret from Twitch Dev
                      Console
                    </li>
                    <li>
                      The curl command in Bot Admin shows how to exchange this
                      code
                    </li>
                    <li>
                      After exchange, you&apos;ll get both access_token and
                      refresh_token
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      Save this token securely - you won&apos;t see it again
                    </li>
                    <li>Never share this token publicly</li>
                    <li>The bot must be a moderator in channels to function</li>
                    <li>
                      To get a refresh token, use the authorization code flow
                      instead
                    </li>
                  </>
                )}
              </ul>
            </div>
          </>
        )}

        <p
          style={{
            textAlign: "center",
            color: "#a0aec0",
            fontSize: "14px",
            marginTop: "20px",
            marginBottom: "10px",
          }}
        >
          You can close this window and return to your main browser window
        </p>
        <button
          onClick={() => window.close()}
          style={{
            width: "100%",
            padding: "12px",
            backgroundColor: "#4a5568",
            color: "white",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontSize: "16px",
            fontWeight: "500",
          }}
        >
          Close This Window
        </button>
      </div>
    </div>
  );
};

export default BotAuthCallback;
